import { FromSmallestUnit } from "@/utils/currency";
import { constants } from "@/utils/constants";
import { isAllDayEvent } from "@/utils/dateutils";
import { calculateRetailTaxTotal } from "@/utils/priceScheduleUtils";
import { EXT_DISNEY_NET_DISCOUNT } from "@/api/booking/Extensions";
import { HHMMFormat } from "@/filters/date";
import { Supplier } from "@/api/suppliers/SupplierModels";
import type { Product } from "@/api/products/ProductModels";
const moment = require("moment-timezone");

/**
 * Formats the given amount in the smallest unit of the specified currency.
 * If the amount or currency are null then "NOT PROVIDED" is returned.
 */
export function formatAmount(amount: any, currency: string) {
  if (amount != null && currency != null) {
    return `${FromSmallestUnit(amount, currency)} ${currency}`;
  }
  return constants.PRICE_NOT_PROVIDED;
}

/**
 * Creates the tax name in the format `Taxes & Fees ($travelerType)'
 */
export function getTaxName(travelerType: string) {
  return `Taxes & Fees (${travelerType})`;
}

/**
 * calculates the total retail tax amount for the price schedule
 * @param priceSchedule
 */
export function getTaxAmount(priceSchedule: any): string {
  return calculateRetailTaxTotal(priceSchedule);
}

/**
 * Determines if any of the price schedules contain included taxes.
 */
export function priceSchedulesContainTaxes(rate: any) {
  for (const priceSchedule of rate.priceSchedule) {
    if (priceSchedule.includedTaxes != null && priceSchedule.includedTaxes.length > 0) {
      return true;
    }
  }
  return false;
}

/**
 * returns the Disney ticket code metadata (aka the unit id)
 * @param pricing - a price schedule or pricing info model
 */
export function getDisneyTicketCode(pricing: any) {
  return `Ticket Code: ${pricing.unitId}`;
}

/**
 * Retrieves the Disney Net Discount extension data if present
 */
export function getDisneyNetDiscount(pricing: any): string | null {
  const netDiscount = pricing.extensions?.[EXT_DISNEY_NET_DISCOUNT] || null;
  if (netDiscount != null) {
    return `Net Discount: ${netDiscount}`;
  }
  return null;
}

/**
 * formatStartTime - formats the start time datetime to just the time using the rate timezone if applicable
 * @param startTime
 * @param rateData
 */
export function formatStartTime(startTime: string, rateData: any): string {
  const localTimezone = rateData?.hours[0]?.timezone || "UTC";
  const formattedStartTime = moment(startTime).tz(localTimezone).format(HHMMFormat);
  return formattedStartTime;
}

/**
 * Generates a string listing the start times based on the availability data provided.
 * returns "All Day" for all-day events, "No Availability" if no valid data exists, or a list of start times otherwise.
 */
export function startTimesText(rateData: any): string {
  const startTimes: string[] = [];
  if (rateData.avData && rateData.avData.length > 0) {
    for (const availability of rateData.avData) {
      if (parseInt(availability.capacity) <= 0) {
        continue;
      }
      if (isAllDayEvent(availability.start, availability.end)) {
        startTimes.push("All Day");
      } else {
        startTimes.push(formatStartTime(availability.start, rateData));
      }
    }
  }
  if (startTimes.length > 0) {
    return "Start Time(s): " + startTimes.join(", ");
  } else {
    return "No Availability";
  }
}

export function availabilityTypeText(type: string) {
  return constants.availabilityTypes[type];
}

// check
/**
 * Builds the option card title, appending availability/price warnings if needed.
 */
export function buildOptionTitle(option: any): string {
  let title = option.cardTitle;
  if (!option.hasAvailability) {
    title += ` [${constants.NO_AVAILABILITY}]`;
  }
  if (!option.hasRetailPricing) {
    title += ` [${constants.NO_PRICES}]`;
  }
  return title;
}

/**
 * Determines if the given option is disabled based on its availability and pricing.
 */
export function isOptionDisabled(option: any): boolean {
  return !option.hasAvailability || !option.hasRetailPricing;
}

export function hasGeneralInfoText(item: any, selectedSupplier: Supplier, selectedProduct: Product): boolean {
  let travelerHasGeneralInfo = false;
  if (item?.prices) {
    item.prices.forEach((price: any) => {
      if (price.metadata?.generalInfo && price.metadata?.generalInfo?.length > 0) {
        travelerHasGeneralInfo = true;
      }
    });
  }
  return (
    (selectedSupplier?.metadata?.generalInfo && selectedSupplier.metadata.generalInfo.length > 0) ||
    (selectedProduct?.metadata?.generalInfo && selectedProduct.metadata.generalInfo.length > 0) ||
    (item?.metadata?.generalInfo && item?.metadata?.generalInfo?.length > 0) ||
    travelerHasGeneralInfo
  );
}

export function hasCategories(item: any, selectedSupplier: Supplier, selectedProduct: Product): boolean {
  let travelerHasCategories = false;
  if (item?.prices) {
    item.prices.forEach((price: any) => {
      if (price.metadata?.categories && price.metadata?.categories?.length > 0) {
        travelerHasCategories = true;
      }
    });
  }
  return (
    (selectedSupplier?.metadata?.categories && selectedSupplier.metadata.categories.length > 0) ||
    (selectedProduct?.metadata?.categories && selectedProduct.metadata.categories.length > 0) ||
    (item?.metadata?.categories && item?.metadata?.categories?.length > 0) ||
    travelerHasCategories
  );
}

/**
 * Returns true if the item has the Disney date-based ticket extension.
 */
export function showValidity(item: any): boolean {
  const { EXT_DISNEY_DATE_BASED_TICKET } = require("@/api/booking/Extensions");
  return item.ext?.[EXT_DISNEY_DATE_BASED_TICKET] === "true";
}

const LOWERCASE_WORDS = new Set(["a", "an", "and", "as", "at", "but", "by", "for", "if", "in", "nor", "of", "on", "or", "per", "the", "to", "vs", "via"]);

export function formatFeatureTitle(title: string | null | undefined): string {
  return (
    title
      ?.toLowerCase()
      .replace(/_/g, " ")
      .split(" ")
      .map((word, index) => {
        if (index !== 0 && LOWERCASE_WORDS.has(word)) {
          return word;
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ") ?? ""
  );
}

export const sortProducts = (products: Product[]) => {
  return [...products].sort((a, b) => {
    const start = (a.name ?? "").toLowerCase().charCodeAt(0);
    const end = (b.name ?? "").toLowerCase().charCodeAt(0);
    return start - end;
  });
};

/**
 * getEmailError - extracts the failure reason from email API error
 */
export const getEmailError = (error: any, fallbackMsg: string) => {
  const reason = error?.response?.data?.message || error?.response?.statusText || error?.message;
  return {
    message: reason ? `${fallbackMsg}: ${reason}` : fallbackMsg,
    details: {
      status: error?.status ?? error?.response?.status,
      statusText: error?.response?.statusText,
      responseData: error?.response?.data,
      error: error?.message,
    },
  };
};
